import React, { useEffect, useRef } from 'react';
import { Order, Driver, SystemConfig } from '../../types';

declare var L: any;

interface Props {
    orders: Order[];
    drivers: Driver[];
    config: SystemConfig;
}

const PROVIDER_COLORS = {
    own: '#6366f1',
    third: '#f97316',
    none: '#64748b'
};

export const AnalyticsMapEngine: React.FC<Props> = ({ orders, drivers, config }) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstance = useRef<any | null>(null);

    // Ref para o grupo de camadas
    const layerGroupRef = useRef<any>(null);

    // 1. Inicialização Única
    useEffect(() => {
        if (!mapRef.current) return;
        if (mapInstance.current) return;

        try {
            mapInstance.current = L.map(mapRef.current, {
                zoomControl: false,
                attributionControl: false
            }).setView([config.businessLat, config.businessLng], 13);

            const theme = config.isSolarMode ? 'light' : 'dark';

            // Tile Layer base sem labels
            L.tileLayer(`https://{s}.basemaps.cartocdn.com/${theme}_nolabels/{z}/{x}/{y}{r}.png`, { opacity: 0.8 }).addTo(mapInstance.current);
            // Labels apenas contexto
            L.tileLayer(`https://{s}.basemaps.cartocdn.com/${theme}_only_labels/{z}/{x}/{y}{r}.png`, { opacity: 0.6 }).addTo(mapInstance.current);

            layerGroupRef.current = L.layerGroup().addTo(mapInstance.current);

            setTimeout(() => { mapInstance.current?.invalidateSize(); }, 250);

        } catch (e) {
            console.error("Leaflet Init Failed", e);
        }

        return () => {
            if (mapInstance.current) {
                mapInstance.current.remove();
                mapInstance.current = null;
            }
        };
    }, []);

    // 2. Atualização de Dados
    useEffect(() => {
        if (!mapInstance.current || !layerGroupRef.current) return;

        layerGroupRef.current.clearLayers();

        const hubIcon = L.divIcon({ className: 'c-hub', html: `<div class="sal-hub-container"><div class="sal-radar-wave"></div><div class="sal-hub-marker">SAL</div></div>`, iconSize: [0, 0] });
        L.marker([config.businessLat, config.businessLng], { icon: hubIcon }).addTo(layerGroupRef.current);

        // --- GRID DENSITY LOGIC ---
        const CELL_SIZE = 0.003; // ~300 metros

        const grid: Record<string, { count: number, lat: number, lng: number, orders: Order[] }> = {};
        let maxCount = 0;

        orders.forEach(order => {
            const lat = order.reconLat || order.lat;
            const lng = order.reconLng || order.lng;
            if (!lat || !lng || order.status === 'archived') return;

            // Snap to grid
            const gridLat = Math.floor(lat / CELL_SIZE) * CELL_SIZE;
            const gridLng = Math.floor(lng / CELL_SIZE) * CELL_SIZE;
            const key = `${gridLat.toFixed(4)}_${gridLng.toFixed(4)}`;

            if (!grid[key]) {
                grid[key] = { count: 0, lat: gridLat, lng: gridLng, orders: [] };
            }
            grid[key].count++;
            grid[key].orders.push(order);
            if (grid[key].count > maxCount) maxCount = grid[key].count;
        });

        Object.values(grid).forEach(cell => {
            const intensity = cell.count / maxCount;
            let color = '#f43f5e'; // Rose (Low Density - CAUTION)
            let opacity = 0.3;

            if (intensity > 0.75) { color = '#2dd4bf'; opacity = 0.8; } // Cyber Teal (Ultra - GREAT)
            else if (intensity > 0.5) { color = '#10b981'; opacity = 0.6; } // Emerald (High)
            else if (intensity > 0.25) { color = '#f97316'; opacity = 0.4; } // Orange (Medium)

            const bounds = [
                [cell.lat, cell.lng],
                [cell.lat + CELL_SIZE, cell.lng + CELL_SIZE]
            ];

            const rect = L.rectangle(bounds, {
                color: 'transparent',
                fillColor: color,
                fillOpacity: opacity,
                weight: 0
            }).addTo(layerGroupRef.current);

            rect.bindPopup(`
                <div style="background: rgb(var(--card-bg)); color: rgb(var(--text-main)); padding: 8px; border-radius: 12px; border: 1px solid rgb(var(--border-main)); box-shadow: var(--shadow-md); text-align: center; min-width: 100px;">
                    <div style="font-weight: 900; font-size: 14px; margin-bottom: 2px; color: ${color};">${cell.count} PEDIDOS</div>
                    <div style="font-size: 10px; opacity: 0.6; font-weight: 700; text-transform: uppercase;">Nesta região</div>
                </div>
            `, { closeButton: false });

            rect.on('mouseover', function (e: any) { this.openPopup(); this.setStyle({ fillOpacity: opacity + 0.2 }); });
            rect.on('mouseout', function (e: any) { this.closePopup(); this.setStyle({ fillOpacity: opacity }); });
        });

    }, [orders, drivers, config]);

    return (
        <div className="relative w-full h-full">
            <div ref={mapRef} className="w-full h-full bg-sal-gunmetal" />

            {/* Legend Overlay */}
            <div className="absolute bottom-6 right-6 z-[400] bg-card-bg/90 dark:bg-sal-gunmetal/90 backdrop-blur-md border border-border-dim p-4 rounded-2xl shadow-2xl">
                <div className="text-xs font-black text-text-primary uppercase tracking-widest mb-3 border-b border-border-dim pb-2">Eficiência Logística</div>
                <div className="space-y-2">
                    <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-[#f43f5e]/80 border border-[#f43f5e]"></div>
                        <div className="text-xs text-rose-500 dark:text-rose-300 font-bold uppercase">Baixa (Ineficiente)</div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-[#f97316]/60 border border-[#f97316]"></div>
                        <div className="text-xs text-text-muted font-bold uppercase">Média</div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-[#10b981]/60 border border-[#10b981]"></div>
                        <div className="text-xs text-text-muted font-bold uppercase">Alta</div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-[#2dd4bf] border border-[#2dd4bf] shadow-[0_0_10px_rgba(45,212,191,0.5)]"></div>
                        <div className="text-xs text-text-primary font-black uppercase">Excelente</div>
                    </div>
                </div>
            </div>
        </div>
    );
};
